import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Search } from 'lucide-react';

import { cn } from '@/lib/utils';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Die Suchleiste über einer Werkzeugtabelle.
 *
 * Vier Tabellen im Bereich *Agentendienste* tragen sie — Werkzeugsätze, Wissensbasen,
 * Prompt-Werkzeuge, Workflows —, und sie sehen dieselbe Beschriftung und dasselbe Feld wie der
 * Filter des Marktplatzes, der schon vorher da war. Vier Kopien desselben Markups wären vier
 * Stellen, an denen die Beschriftung irgendwann auseinanderläuft.
 *
 * **Das Blättern wird zurückgesetzt, sobald sich der Filter ändert.** Die Seitenzahl steht in der
 * Adresse, die gefilterte Liste ist kürzer — ohne das Zurücksetzen bleibt man auf Seite 3 einer
 * Liste, die nur noch eine hat, und sieht eine leere Tabelle statt seines Treffers.
 */
export function NameFilterBar({
  wert,
  onChange,
  seite,
  onSeiteChange,
  anzahl,
  gesamt,
  className,
}: {
  wert: string;
  onChange: (wert: string) => void;
  /** Aktuelle Seite (1-basiert) und ihr Setzer — aus `useUrlPageState`. */
  seite?: number;
  onSeiteChange?: (seite: number) => void;
  /** Wie viele Einträge die Tabelle zeigt und wie viele es insgesamt sind. */
  anzahl?: number;
  gesamt?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const feldId = useId();

  const setzen = (neu: string) => {
    onChange(neu);
    if (seite !== undefined && seite !== 1) onSeiteChange?.(1);
  };

  return (
    <div className={cn('flex min-w-0 flex-1 items-center gap-3', className)}>
      {/* Ohne Beschriftung darüber: Die Leiste steht in einer Zeile mit dem Anlegen-Knopf, und
          eine zweite Zeile nur für das Wort „Filter" kostet Platz, den die Tabelle braucht.
          Der Platzhalter sagt dasselbe, das `aria-label` sagt es den Vorlesegeräten. */}
      <Label htmlFor={feldId} className="sr-only">
        {t('ui.enterFilter')}
      </Label>
      <div className="relative min-w-[180px] max-w-[320px] flex-1">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={feldId}
          className="h-9 pl-7"
          value={wert}
          onChange={event => setzen(event.target.value)}
          placeholder={t('ui.filterByName')}
          aria-label={t('ui.enterFilter')}
        />
      </div>
      {wert.trim() !== '' && anzahl !== undefined && gesamt !== undefined && (
        <span className="whitespace-nowrap text-sm text-muted-foreground">
          {t('ui.entriesDisplayed', { shown: anzahl, total: gesamt })}
        </span>
      )}
    </div>
  );
}

export default NameFilterBar;
